import { useFilters } from "@/context/FilterContext";

interface PaginationProps {
  totalItems: number;
}

const btnBase =
  "px-[10px] py-1 border border-border rounded-default bg-transparent text-text2 cursor-pointer text-xs transition-colors duration-[120ms] hover:border-accent hover:text-text disabled:opacity-30 disabled:cursor-not-allowed";

export function Pagination({ totalItems }: PaginationProps) {
  const { page, pageSize, setPage, setPageSize } = useFilters();
  const totalPages = Math.ceil(totalItems / pageSize);
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, totalItems);

  if (totalItems === 0) return null;

  const pages = getPageNumbers(page, totalPages);

  return (
    <div className="flex items-center justify-between px-[28px] py-[10px] border-t border-border text-xs text-text2">
      <span>{start}–{end} of {totalItems}</span>
      <div className="flex gap-1">
        <button
          className={btnBase}
          onClick={() => setPage(page - 1)}
          disabled={page === 0}
        >
          ‹
        </button>
        {pages.map((p, i) =>
          typeof p === "number" ? (
            <button
              key={i}
              onClick={() => setPage(p)}
              className={`${btnBase}${p === page ? " border-accent text-accent bg-blue-500/[0.08]" : ""}`}
            >
              {p + 1}
            </button>
          ) : (
            <span key={i} className="px-1 text-text2">…</span>
          )
        )}
        <button
          className={btnBase}
          onClick={() => setPage(page + 1)}
          disabled={page >= totalPages - 1}
        >
          ›
        </button>
      </div>
      <select
        value={pageSize}
        onChange={e => setPageSize(Number(e.target.value))}
        className="px-2 py-1 bg-bg border border-border rounded-default text-text text-[11px]"
      >
        {[10, 25, 50, 100].map(s => (
          <option key={s} value={s}>{s} / page</option>
        ))}
      </select>
    </div>
  );
}

function getPageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const pages: (number | "...")[] = [0];
  if (current > 2) pages.push("...");
  for (let i = Math.max(1, current - 1); i <= Math.min(total - 2, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 3) pages.push("...");
  pages.push(total - 1);
  return pages;
}
